import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase, Blog, Tag } from '@/lib/supabase';
import { CalendarDays, Clock, ChevronLeft, Tag as TagIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<Blog[]>([]);

  useEffect(() => {
    async function fetchBlogData() {
      try {
        setLoading(true);
        
        // Fetch the blog post by slug
        const { data: blogData, error: blogError } = await supabase
          .from('blogs')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .single();
        
        if (blogError) throw blogError;
        
        if (blogData) {
          setBlog(blogData as Blog);
          
          // Fetch tags for this blog
          const { data: blogTagsData, error: blogTagsError } = await supabase
            .from('blog_tags')
            .select('tag_id')
            .eq('blog_id', blogData.id);
            
          if (blogTagsError) throw blogTagsError;
          
          if (blogTagsData && blogTagsData.length > 0) {
            const tagIds = blogTagsData.map(bt => bt.tag_id);
            
            const { data: tagsData, error: tagsError } = await supabase
              .from('tags')
              .select('*')
              .in('id', tagIds);
              
            if (tagsError) throw tagsError;
            setTags(tagsData as Tag[]);
            
            // Fetch related posts that share the same tags
            const { data: relatedTagsData, error: relatedTagsError } = await supabase
              .from('blog_tags')
              .select('blog_id')
              .in('tag_id', tagIds)
              .neq('blog_id', blogData.id);
              
            if (relatedTagsError) throw relatedTagsError;
            
            if (relatedTagsData && relatedTagsData.length > 0) {
              const relatedBlogIds = [...new Set(relatedTagsData.map(item => item.blog_id))];
              
              const { data: relatedBlogsData, error: relatedBlogsError } = await supabase
                .from('blogs')
                .select('*')
                .in('id', relatedBlogIds)
                .eq('published', true)
                .order('published_at', { ascending: false })
                .limit(3);
                
              if (relatedBlogsError) throw relatedBlogsError;
              setRelatedPosts(relatedBlogsData as Blog[]);
            }
          }
        } else {
          // No blog found with this slug
          setError('Blog post not found');
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
    
    if (slug) {
      fetchBlogData();
    }
  }, [slug]);

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown date';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (e) {
      return 'Unknown date';
    }
  };

  // Calculate read time based on content length
  const getReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return readTime < 1 ? 1 : readTime;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin w-12 h-12 border-4 border-teal-400 border-t-transparent rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gray-900 py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-white mb-6">Error</h1>
          <p className="text-gray-300 mb-8">{error || 'Blog post not found'}</p>
          <Button onClick={() => navigate('/blog')}>
            Return to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-32 pb-16 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            className="text-teal-400 hover:text-teal-300 p-0 flex items-center"
            onClick={() => navigate('/blog')}
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back to all articles
          </Button>
        </motion.div>

        {/* Blog post content */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          {/* Cover image */}
          <div className="w-full aspect-[21/9] overflow-hidden rounded-xl mb-8">
            <img
              src={blog.cover_image}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Blog header */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {blog.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-6">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-teal-400" />
                <span>{blog.published_at ? formatDate(blog.published_at) : formatDate(blog.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-400" />
                <span>{getReadTime(blog.content)} min read</span>
              </div>
            </div>
            
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <span 
                    key={tag.id} 
                    className="px-3 py-1 bg-teal-400/20 text-teal-300 rounded-full flex items-center text-sm"
                  >
                    <TagIcon className="w-3 h-3 mr-1" />
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </header>

          {/* Blog summary */}
          <div className="text-xl text-gray-300 mb-8 border-l-4 border-teal-400 pl-6 py-2 italic">
            {blog.summary}
          </div>

          {/* Blog content */}
          <div className="prose prose-lg prose-invert max-w-none">
            {blog.content.split('\n').map((paragraph, index) => 
              paragraph ? <p key={index}>{paragraph}</p> : <br key={index} />
            )}
          </div>
        </motion.article>

        {/* Related posts */}
        {relatedPosts.length > 0 && (
          <motion.div
            className="mt-16 pt-12 border-t border-gray-800"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <h2 className="text-2xl font-bold text-white mb-8">Related Articles</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((post) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  className="group bg-gray-800/50 rounded-lg overflow-hidden flex flex-col h-full"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  
                  <div className="p-4 flex flex-col flex-grow">
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-teal-400 transition-colors">
                      {post.title}
                    </h3>
                    
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                      {post.summary}
                    </p>
                    
                    <div className="mt-auto text-xs text-gray-500">
                      {post.published_at ? formatDate(post.published_at) : formatDate(post.created_at)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BlogPost; 